import { cn } from '@rocco/utils/cn';
import { RoccoLink } from '@rocco/components/link';

import type { B2BFooterConfig, B2BFooterLinkGroup } from '../types';
import { B2BSubscribeForm } from './B2BSubscribeForm';

export interface B2BFooterProps {
    config: B2BFooterConfig;
    /** Resolved path for the large ARCHIPRO wordmark; falls back to text. */
    wordmarkSrc?: string;
    className?: string;
}

/* -------------------------------------------------------------------------- */
/* Layout constants — pulled from Figma `517:24650` (1920 × 1085).            */
/*                                                                            */
/* The footer is absolute-positioned to the 1920 frame, like the rest of the  */
/* B2B page. Five named link groups slot into three Figma positions:          */
/*   • Discover, Resources, Business → centre column (left 972px)             */
/*   • ArchiPro                       → right column top (left 1432px)        */
/*   • Connect                        → right column lower (left 1432px)      */
/*                                                                            */
/* The eyebrow heading is 16px Roman opacity-50; each link is 16px Medium;    */
/* gap between links is 10px; tracking 0.32px ≈ 0.02em.                       */
/* -------------------------------------------------------------------------- */

const FRAME_W = 1920;
const FRAME_H = 1085;

/** px → vw at the 1920 design width. */
const vw = (px: number) => `${(px / FRAME_W) * 100}vw`;
/** px → % of the 1920 design width (for absolute left/width). */
const pctW = (px: number) => `${(px / FRAME_W) * 100}%`;
/** px → % of the 1085 design height (for absolute top). */
const pctH = (px: number) => `${(px / FRAME_H) * 100}%`;

interface LinkColumnProps {
    group: B2BFooterLinkGroup | undefined;
    leftPx: number;
    topPx: number;
}

/**
 * A single Figma footer link group: opacity-50 Roman heading, then a stack of
 * Medium link items with 10px between them. Renders nothing if the named group
 * isn't found in the config (defensive — keeps the layout from crashing if a
 * group is removed).
 */
const LinkColumn = ({ group, leftPx, topPx }: LinkColumnProps) => {
    if (!group) return null;
    return (
        <div
            className="absolute font-sans text-[#141414] max-md:static"
            style={{ left: pctW(leftPx), top: pctH(topPx) }}
        >
            <p
                className={cn(
                    'mb-[1.042vw] text-[0.833vw] tracking-[0.02em] opacity-50',
                    'max-md:mb-3 max-md:text-sm',
                )}
            >
                {group.heading}
            </p>
            <ul className={cn('flex flex-col gap-[0.521vw]', 'max-md:gap-2')}>
                {group.links.map((link) => (
                    <li key={`${link.label}-${link.href}`}>
                        <RoccoLink
                            to={link.href}
                            newTab={link.newTab}
                            isNonRemixRoute={link.isNonRemixRoute}
                            className={cn(
                                'text-[0.833vw] font-medium leading-[1.3] tracking-[0.02em] transition-opacity hover:opacity-60',
                                'max-md:text-sm',
                            )}
                        >
                            {link.label}
                        </RoccoLink>
                    </li>
                ))}
            </ul>
        </div>
    );
};

/**
 * B2B page footer — Figma node `517:24650`.
 *
 * Absolute-positioned to the 1920 frame (`aspect-1920/1085`); two thin
 * vertical dividers at x = 948 px and x = 1411 px; Subscribe block (top-left)
 * with a 440 × 54 pill input centred under it; three named link groups stacked
 * down the centre (Discover, Resources, Business); two named link groups
 * stacked on the right (ArchiPro, Connect); large dark ARCHIPRO wordmark
 * bottom-left; copyright centre-bottom; legal links bottom-right.
 *
 * Mobile (`max-md:`) collapses to a single vertical stack.
 */
export const B2BFooter = ({
    config,
    wordmarkSrc,
    className,
}: B2BFooterProps) => {
    // Look up the named groups so the absolute positions never drift if the
    // config order changes. Missing groups stay undefined and LinkColumn
    // renders nothing — keeps the footer resilient to BC-4427-style omissions
    // (e.g. Resources hidden on US).
    const byHeading = new Map<string, B2BFooterLinkGroup>(
        config.linkGroups.map((g) => [g.heading.toLowerCase(), g]),
    );
    const pick = (heading: string) => byHeading.get(heading.toLowerCase());

    const discover = pick('Discover');
    const resources = pick('Resources');
    const business = pick('Business');
    const archipro = pick('ArchiPro');
    const connect = pick('Connect');

    return (
        <footer
            data-b2b-footer
            className={cn(
                'relative w-full bg-[#fafafa] text-[#141414]',
                'aspect-1920/1085',
                'max-md:aspect-auto max-md:px-6 max-md:pb-10 max-md:pt-16',
                className,
            )}
        >
            {/* Vertical dividers — Figma `517:24653` / `517:24654`. */}
            <div
                aria-hidden
                className="absolute top-0 h-full w-px bg-black/10 max-md:hidden"
                style={{ left: pctW(948) }}
            />
            <div
                aria-hidden
                className="absolute top-0 h-full w-px bg-black/10 max-md:hidden"
                style={{ left: pctW(1411) }}
            />

            {/* Subscribe block (left, top). Heading + blurb + input pill. */}
            <div
                className={cn(
                    'absolute font-sans text-[#141414]',
                    'max-md:static max-md:mb-12 max-md:flex max-md:flex-col max-md:gap-5',
                )}
                style={{ left: pctW(53), top: pctH(181) }}
            >
                <p
                    className={cn(
                        'text-[0.833vw] tracking-[0.02em] opacity-50',
                        'max-md:text-sm',
                    )}
                >
                    {config.subscribeHeading}
                </p>
                <p
                    className={cn(
                        'mt-[1.042vw] w-[22.5vw] text-[0.833vw] font-medium leading-[1.3] tracking-[0.02em]',
                        'max-md:mt-0 max-md:w-full max-md:text-sm',
                    )}
                >
                    {config.subscribeBlurb}
                </p>
                <div
                    className={cn('absolute', 'max-md:static max-md:mt-2')}
                    style={{
                        // Pill sits at top=312.88 in the 1085 frame; this
                        // container starts at top=181, so offset = 131.88px.
                        top: vw(131.88),
                        // Pill is centred on x=270.06 (calc(50% - 689.94px)
                        // with a -translate-x-1/2 wrapper); container starts
                        // at left=53, pill width=440, so its left edge is
                        // (270.06 - 220) - 53 = -2.94px.
                        left: vw(-2.94),
                    }}
                >
                    <B2BSubscribeForm
                        placeholder={config.subscribePlaceholder}
                    />
                </div>
            </div>

            {/* Centre column — Discover / Resources / Business. */}
            <div className="max-md:mb-8 max-md:flex max-md:flex-col max-md:gap-8">
                <LinkColumn group={discover} leftPx={972} topPx={181} />
                <LinkColumn group={resources} leftPx={972} topPx={418} />
                <LinkColumn group={business} leftPx={972} topPx={562} />
            </div>

            {/* Right column — ArchiPro / Connect. */}
            <div className="max-md:mb-12 max-md:flex max-md:flex-col max-md:gap-8">
                <LinkColumn group={archipro} leftPx={1432} topPx={181} />
                <LinkColumn group={connect} leftPx={1432} topPx={414} />
            </div>

            {/* Wordmark — bottom-left (Figma `517:24703`, 377.4 × 49). */}
            <div
                className={cn(
                    'absolute',
                    'max-md:static max-md:mb-6 max-md:mt-8',
                )}
                style={{
                    left: pctW(59),
                    top: pctH(980),
                    width: pctW(377.395),
                    aspectRatio: '377.395 / 48.971',
                }}
            >
                {wordmarkSrc ? (
                    <img
                        src={wordmarkSrc}
                        alt="ArchiPro"
                        className="block size-full object-contain object-left"
                    />
                ) : (
                    <span className="font-sans text-[3.073vw] font-bold uppercase leading-none tracking-tight text-[#141414] max-md:text-3xl">
                        ArchiPro
                    </span>
                )}
            </div>

            {/* Copyright — centre, slightly below wordmark baseline. */}
            <p
                className={cn(
                    'absolute font-sans text-[0.833vw] tracking-[0.02em] text-[#141414] opacity-50',
                    'max-md:static max-md:mt-4 max-md:text-xs',
                )}
                style={{ left: pctW(970), top: pctH(999) }}
            >
                {config.copyright}
            </p>

            {/* Legal links — bottom-right column. */}
            {config.legalLinks && config.legalLinks.length > 0 && (
                <div
                    className={cn(
                        'absolute flex flex-col gap-[0.573vw] font-sans text-[0.833vw] tracking-[0.02em] text-[#141414]',
                        'max-md:static max-md:mt-2 max-md:flex-row max-md:gap-4 max-md:text-xs',
                    )}
                    style={{ left: pctW(1434), top: pctH(976) }}
                >
                    {config.legalLinks.map((link) => (
                        <RoccoLink
                            key={link.label}
                            to={link.href}
                            newTab={link.newTab}
                            isNonRemixRoute={link.isNonRemixRoute}
                            className="opacity-50 transition-opacity hover:opacity-80"
                        >
                            {link.label}
                        </RoccoLink>
                    ))}
                </div>
            )}
        </footer>
    );
};
